import { isUnimplemented } from "./capacitorUnimplemented";

export type MediaVerification =
  | { playable: true; durationSeconds: number | null }
  | { playable: false; reason: string };

// Learned from the first real call, like downloadBatchBridge's support map:
// the Capacitor plugin proxy answers every property, so only an UNIMPLEMENTED
// rejection reveals a native build without the method — and that build gives
// the same answer for the rest of the session.
let nativeVerifyUnimplemented = false;

/**
 * Whether AVFoundation can open a downloaded file. Null means unverifiable —
 * a build whose native player predates `verifyMedia`, the web, or a failed
 * bridge call — which callers must treat as "no verdict", never as broken:
 * deleting a good offline copy because the check itself failed would turn a
 * flaky bridge into a re-download loop.
 */
export async function verifyDownloadedMedia(options: {
  uri: string;
  mimeType: string | null;
}) {
  if (nativeVerifyUnimplemented) {
    return null;
  }

  try {
    // Loaded on demand: the plugin wrapper registers itself with
    // `@capacitor/core` at evaluation, and the download engine's import graph
    // reaches test processes that stub that module without `registerPlugin`.
    const { NativeVideoPlayer } = await import("./NativeVideoPlayer");
    const result = await NativeVideoPlayer.verifyMedia({
      url: options.uri,
      ...(options.mimeType ? { mimeType: options.mimeType } : {}),
    });

    if (!result.playable) {
      return {
        playable: false,
        reason: result.error ?? "unknown",
      } satisfies MediaVerification;
    }

    return {
      playable: true,
      durationSeconds: result.durationSeconds ?? null,
    } satisfies MediaVerification;
  } catch (error) {
    if (isUnimplemented(error)) {
      nativeVerifyUnimplemented = true;
    } else {
      console.warn("[media-verification] verifyMedia failed", error);
    }

    return null;
  }
}

/** Test seam: forget that an earlier call found verifyMedia unimplemented. */
export function resetMediaVerificationSupportCache() {
  nativeVerifyUnimplemented = false;
}
